import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { IconButton } from '../../../Components/Buttons';
import { Icon } from '../../../Components/Icons';
import { Tab, Tabs } from '../../../Components/Tabs';
import { useCareTypeConfigContext } from '../../../Components/CareTypeConfigContext';
import { Text } from '../../../globalStyles';
import { CareType, Contract, GetContractCareTypeUtilizationQuery } from '../../../graphQL';
import * as Styles from '../../Home/styles';
import { ContractPeriodSubset } from '../ContractsPage';
import { Nullable } from '../../../types';
import { AddEditContractModal } from './AddEditContractModal';
import { ContractCareTypeUtilizationContent } from './ContractCareTypeUtilizationContent';

interface CarePeriodTextProps {
  contractPeriod: ContractPeriodSubset;
}

const ContractPeriodText = ({ contractPeriod }: CarePeriodTextProps) => {
  return <>{formatContractPeriodText(contractPeriod)}</>;
};

const formatContractPeriodText = (contractPeriod: ContractPeriodSubset) => {
  return `${moment(contractPeriod.startDate).format('M/D/YYYY')}
    - ${moment(contractPeriod.endDate).format('M/D/YYYY')}`;
};

const TabsComponent = ({
  activeTab,
  setActiveTab,
  tabs,
}: {
  activeTab: string;
  setActiveTab: React.Dispatch<React.SetStateAction<string | undefined>>;
  tabs: Tab<string>[];
}) => {
  return (
    <Tabs
      activeTab={activeTab}
      initialTab={activeTab}
      config={{ tabpanel: ({ children }) => <div className="pa4">{children}</div> }}
      onChange={tab => setActiveTab(tab)}
      tabs={tabs}
    />
  );
};

type ContractDashboardContract = Pick<Contract, 'id' | 'scope' | 'careTypes'>;
type ContractCareTypeUtilizationContentData = Extract<
  GetContractCareTypeUtilizationQuery['getContractCareTypeUtilization'],
  { __typename: 'ContractCareTypeUtilizationContent' }
>;

export const ContractDashboard = ({
  contract,
  careTypes,
  editable,
  onChangeContract,
}: {
  contract: Nullable<ContractDashboardContract>;
  careTypes: CareType[];
  editable: boolean;
  onChangeContract: (changedContractId: number | undefined) => void;
}) => {
  const { getCareTypeDisplay } = useCareTypeConfigContext();
  const [activeTab, setActiveTab] = useState<string>();
  const [isOpen, setIsOpen] = useState(false);
  const [utilizationContentByCareType, setUtilizationContentByCareType] = useState<
    Partial<Record<CareType, ContractCareTypeUtilizationContentData>>
  >({});

  const contractId = contract?.id;

  useEffect(() => {
    setUtilizationContentByCareType({});
  }, [contractId]);

  const handleUtilizationContentLoaded = useCallback(
    (careType: CareType, content: ContractCareTypeUtilizationContentData) => {
      setUtilizationContentByCareType(current =>
        current[careType] === content
          ? current
          : {
              ...current,
              [careType]: content,
            }
      );
    },
    []
  );

  if (!contract) {
    return null;
  }

  const tabs: Tab<string>[] = careTypes.map(careType => ({
    title: getCareTypeDisplay(careType).serviceTitle,
    key: careType,
    render: () => {
      return (
        <ContractCareTypeUtilizationContent
          contractId={contract.id}
          careType={careType}
          careTypeScopes={contract.careTypes}
          cachedUtilizationContent={utilizationContentByCareType[careType]}
          onUtilizationContentLoaded={handleUtilizationContentLoaded}
          editable={editable}
        />
      );
    },
  }));

  // Remount the tab list when the available care types change so the tab
  // component drops any internal state for tabs that no longer exist.
  const tabsStateKey = tabs.map(tab => tab.key).join('|');
  const validActiveTab = tabs.some(tab => tab.key === activeTab) ? activeTab : tabs[0]?.key;

  if (!validActiveTab) {
    return null;
  }

  return (
    <Styles.SectionContainer>
      <div className="mt3 mb4 flex items-end">
        <Text.h2>
          Contract Period:{' '}
          <ContractPeriodText
            contractPeriod={{
              startDate: contract.scope.startDate,
              endDate: contract.scope.endDate,
            }}
          />
        </Text.h2>

        {/* Add Logic to Open Modal */}
        <IconButton className="ml-auto">
          <div className=" flex flex-row items-center">
            <Icon icon="iconsBlackEditSvg" size={16} className="mr2" />
            <Text.bodyBold onClick={() => setIsOpen(true)}>Edit Contract</Text.bodyBold>
          </div>
        </IconButton>
        <AddEditContractModal
          careTypes={careTypes}
          contract={contract}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          onSave={onChangeContract}
        />
      </div>
      <TabsComponent
        key={tabsStateKey}
        activeTab={validActiveTab}
        setActiveTab={setActiveTab}
        tabs={tabs}
      />
    </Styles.SectionContainer>
  );
};
